import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FaMapMarkedAlt, FaDirections, FaExternalLinkAlt } from 'react-icons/fa';
import googleMapsService from './GoogleMapsService';

// Map Component
export const GoogleMap = React.memo(({ 
  center, 
  zoom = 15, 
  markers = [], 
  directions = null,
  height = '400px',
  width = '100%',
  onMapLoad = null,
  ...props 
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeMap = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!mapRef.current) return;

        // Clear any existing map instance
        if (mapInstanceRef.current) {
          // Clean up existing map if needed
          mapInstanceRef.current = null;
        }

        const map = await googleMapsService.initializeMap(mapRef.current, {
          center,
          zoom,
          ...props
        });

        mapInstanceRef.current = map;

        // Add markers asynchronously
        if (markers.length > 0) {
          const markerPromises = markers.map(markerData => 
            googleMapsService.addMarker(map, markerData.position, markerData.options)
          );
          await Promise.all(markerPromises);
        }

        // Display directions if provided
        if (directions) {
          await googleMapsService.displayDirections(
            map, 
            directions.origin, 
            directions.destination, 
            directions.travelMode
          );
        }

        if (onMapLoad) {
          onMapLoad(map);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError(err.message || 'Failed to load map');
        setIsLoading(false);
      }
    };

    // Only initialize if we have a valid center
    if (center && center.lat && center.lng) {
      initializeMap();
    } else {
      setError('Invalid map center coordinates');
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        // Clean up map instance if needed
        mapInstanceRef.current = null;
      }
    };
  }, [
    // Only re-run if these specific values change
    center?.lat, 
    center?.lng, 
    zoom,
    // Use JSON.stringify for complex objects to prevent unnecessary re-renders
    JSON.stringify(markers.map(m => ({ lat: m.position.lat, lng: m.position.lng }))),
    directions?.origin,
    directions?.destination,
    directions?.travelMode
  ]);

  return (
    <Box position="relative" width={width} height={height}>
      {isLoading && (
        <Flex
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1}
        >
          <Spinner size="lg" color="#817EFF" />
        </Flex>
      )}
      
      {error && (
        <Alert status="error" position="absolute" top={2} left={2} right={2} zIndex={2}>
          <AlertIcon />
          <AlertTitle>Map Error!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Box
        ref={mapRef}
        width="100%"
        height="100%"
        borderRadius="12px"
        overflow="hidden"
        opacity={isLoading ? 0.5 : 1}
        transition="opacity 0.3s ease"
      />
    </Box>
  );
});

// Directions Component
export const DirectionsComponent = ({ 
  origin, 
  destination, 
  travelMode = 'DRIVING',
  showMap = true,
  height = '400px',
  onDirectionsLoad = null 
}) => {
  const [directions, setDirections] = useState(null);
  const [travelInfo, setTravelInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getDirections = async () => {
    if (!origin || !destination) return;

    try {
      setIsLoading(true);
      setError(null);

      const travelTime = await googleMapsService.getTravelTime(origin, destination, travelMode);
      setTravelInfo(travelTime);

      if (showMap) {
        setDirections({
          origin,
          destination,
          travelMode
        });
      }

      if (onDirectionsLoad) {
        onDirectionsLoad(travelTime);
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Error getting directions:', err);
      setError('Failed to get directions');
      setIsLoading(false);
    }
  };

  const openInMapsApp = () => {
    googleMapsService.openInMapsApp(origin, destination, travelMode.toLowerCase());
  };

  useEffect(() => {
    getDirections();
  }, [origin, destination, travelMode]);

  return (
    <Box>
      {showMap && directions && (
        <GoogleMap
          center={origin}
          directions={directions}
          height={height}
          markers={[
            { position: origin, options: { title: 'Origin' } },
            { position: destination, options: { title: 'Destination' } }
          ]}
        />
      )}

      {travelInfo && (
        <Box mt={4} p={4} bg="rgba(255, 255, 255, 0.05)" borderRadius="12px" border="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="16px" fontWeight="700" color="white">
              <Icon as={FaDirections} mr={2} />
              Travel Information
            </Text>
            <Button
              size="sm"
              leftIcon={<FaExternalLinkAlt />}
              onClick={openInMapsApp}
              bg="#817EFF"
              color="white"
              _hover={{ bg: "#615edc" }}
            >
              Open in Maps
            </Button>
          </Flex>
          
          <Flex gap={6}>
            <Box>
              <Text fontSize="12px" color="gray.300" fontWeight={600}>
                Duration
              </Text>
              <Text fontSize="18px" fontWeight="800" color="white">
                {travelInfo.duration}
              </Text>
            </Box>
            
            <Box>
              <Text fontSize="12px" color="gray.300" fontWeight={600}>
                Distance
              </Text>
              <Text fontSize="18px" fontWeight="800" color="white">
                {travelInfo.distance}
              </Text>
            </Box>
            
            <Box>
              <Text fontSize="12px" color="gray.300" fontWeight={600}>
                Mode
              </Text>
              <Text fontSize="18px" fontWeight="800" color="white" textTransform="capitalize">
                {travelMode.toLowerCase()}
              </Text>
            </Box>
          </Flex>
        </Box>
      )}

      {isLoading && (
        <Flex justify="center" align="center" py={8}>
          <Spinner size="lg" color="#817EFF" />
          <Text ml={3} color="gray.300">Getting directions...</Text>
        </Flex>
      )}

      {error && (
        <Alert status="error" mt={4}>
          <AlertIcon />
          <AlertTitle>Directions Error!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </Box>
  );
};

// Location Picker Component
export const LocationPicker = ({ 
  onLocationSelect, 
  placeholder = "Enter an address...",
  height = '400px' 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const searchLocation = async () => {
    if (!searchQuery.trim()) return;

    try {
      setIsLoading(true);
      const location = await googleMapsService.geocodeAddress(searchQuery);
      setSelectedLocation(location);
      
      if (onLocationSelect) {
        onLocationSelect(location);
      }
    } catch (err) {
      console.error('Error searching location:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Flex gap={2} mb={4}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'white',
            fontSize: '14px'
          }}
          onKeyPress={(e) => e.key === 'Enter' && searchLocation()}
        />
        <Button
          onClick={searchLocation}
          isLoading={isLoading}
          bg="#817EFF"
          color="white"
          _hover={{ bg: "#615edc" }}
        >
          <Icon as={FaMapMarkedAlt} />
        </Button>
      </Flex>

      {selectedLocation && (
        <GoogleMap
          center={selectedLocation}
          height={height}
          markers={[
            { 
              position: selectedLocation, 
              options: { 
                title: selectedLocation.formattedAddress 
              } 
            }
          ]}
        />
      )}
    </Box>
  );
};

export default {
  GoogleMap,
  DirectionsComponent,
  LocationPicker
}; 